import React, { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { EditorState, Modifier } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useSnackbar } from '../../../../global/config/useSnackbar';

const MERGE_TAGS = ['{{FNAME}}', '{{ORG_NAME}}', '{{URL}}'];

interface Props {
  open: boolean;
  onClose: () => void;
  volunteerIds: string[];
  onSuccess: () => void;
}

export const SendPasswordResetDialog: React.FC<Props> = ({ open, onClose, volunteerIds, onSuccess }) => {
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState(EditorState.createEmpty());
  const [submitting, setSubmitting] = useState(false);

  const { run } = useCampfireFetch({ defer: true });

  const insertMergeTag = (tag: string) => {
    const newContent = Modifier.insertText(content.getCurrentContent(), content.getSelection(), tag);
    setContent(EditorState.push(content, newContent, 'insert-characters'));
  };

  const handleClose = () => {
    setSubject('');
    setContent(EditorState.createEmpty());
    onClose();
  };

  const handleSubmit = () => {
    setSubmitting(true);
    run({
      url: `${apiUrl}/vm/volunteer/send-password-reset`,
      method: 'post',
      data: {
        volunteerIds,
        subject,
        content: stateToHTML(content.getCurrentContent()),
      },
    })
      .then((response: any) => {
        if (!response?.ok) {
          setSnackbar({ variant: 'error', message: 'Failed to send password reset email', open: true });
          return;
        }
        const failed = response?.data?.data?.failed ?? [];
        setSnackbar({
          variant: failed.length > 0 ? 'warning' : 'success',
          message:
            failed.length > 0
              ? `Password reset email sent, ${failed.length} volunteer(s) skipped`
              : 'Password reset email sent',
          open: true,
        });
        setSubject('');
        setContent(EditorState.createEmpty());
        onSuccess();
        onClose();
      })
      .finally(() => setSubmitting(false));
  };

  const canSend =
    !submitting && volunteerIds.length > 0 && subject.trim() !== '' && content.getCurrentContent().hasText();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Send password reset</DialogTitle>
      <DialogContent>
        <Typography variant='body2' style={{ marginBottom: '1rem' }}>
          {`${volunteerIds.length} volunteer(s) will be emailed. Each gets their own reset link, valid for 30 days.`}
        </Typography>
        <TextField
          label='Subject'
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          fullWidth
          variant='outlined'
          size='small'
          style={{ marginBottom: '1rem' }}
        />
        <Typography variant='subtitle2' style={{ fontWeight: 700, marginBottom: '0.5rem' }}>
          Merge tags (click to insert)
        </Typography>
        <Box style={{ marginBottom: '0.75rem' }}>
          {MERGE_TAGS.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size='small'
              onClick={() => insertMergeTag(tag)}
              clickable
              variant='outlined'
              style={{ marginRight: 4 }}
            />
          ))}
        </Box>
        <CampfireRichEditor
          placeholder='Write your email. Insert {{URL}} where you want the password reset link to appear...'
          editorState={content}
          setEditorState={setContent}
        />
      </DialogContent>
      <DialogActions>
        <TabletButton onClick={handleClose} disabled={submitting}>
          Cancel
        </TabletButton>
        <TabletButton variant='contained' color='primary' onClick={handleSubmit} disabled={!canSend}>
          {submitting ? (
            <Box display='flex' alignItems='center'>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              Sending
            </Box>
          ) : (
            'Send'
          )}
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
};
